// services/video.service.ts
import { getApi } from "./resonanceApi";

export type VideoMeta = {
  uid: string;
  hlsUrl: string;
  thumbnailUrl: string;
  duration?: number | null;
  created?: string | null;
  meta?: any;
};

export type VideosListResponse = {
  items: VideoMeta[];
  nextCursor: string | null;
};

export type VideoContextResponse = {
  current: VideoMeta;
  items: VideoMeta[];         // the page of videos in the requested direction
  index: number;              // index of `current` in the full filtered list (or -1 if unknown)
  total: number;              // total that match the filter
  nextCursor: string | null;  // cursor for paging further in the same direction
  direction?: "next" | "prev";
};

export async function getVideos(params?: {
  tags?: string[];
  limit?: number;
  cursor?: string;
}) {
  const sp = new URLSearchParams();
  if (params?.tags?.length) sp.set("tags", params.tags.join(","));
  if (params?.limit) sp.set("limit", String(params.limit));
  if (params?.cursor) sp.set("cursor", params.cursor);

  const { data } = await getApi<VideosListResponse>(`videos?${sp.toString()}`);
  return data;
}

export async function getVideo(id: string) {
  const { data } = await getApi<VideoMeta>(`videos/${encodeURIComponent(id)}`);
  return data;
}

/**
 * Fetch a page of videos relative to an anchor `id`.
 * - direction "next" (default): items after `id`
 * - direction "prev": items before `id` (returned in scroll-ready order)
 */
export async function getVideoContext(
  id: string,
  params?: { tags?: string[]; limit?: number; direction?: "next" | "prev" }
) {
  const sp = new URLSearchParams({ current: id });
  if (params?.tags?.length) sp.set("tags", params.tags.join(","));
  if (params?.limit) sp.set("limit", String(params.limit));
  if (params?.direction) sp.set("direction", params.direction);

  const { data } = await getApi<VideoContextResponse>(`videos/context?${sp.toString()}`);
  return data;
}

// Optional tiny helpers if you like:
export const getNextFrom = (id: string, p?: { tags?: string[]; limit?: number }) =>
  getVideoContext(id, { ...p, direction: "next" });

export const getPrevFrom = (id: string, p?: { tags?: string[]; limit?: number }) =>
  getVideoContext(id, { ...p, direction: "prev" });
